import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import { Badge, Box, Button, Flex, HStack, Tab, TabList, TabPanel, TabPanels, Tabs, Text, VStack, useTheme } from "@chakra-ui/react";
import { formatDate } from "utils";
import { Trash, DownloadSimple, MinusCircle, ArrowCounterClockwise, FileText, UserCircle, CalendarBlank, Sparkle, GlobeHemisphereWest, WarningDiamond } from "@phosphor-icons/react";
import { getFileType, getFolderForRound, KB_FOLDER } from "api/common/fileinfo";
import fileinfoService from "api/common/fileinfo.service";
import {
  apiAddToFolders,
  apiArchiveFileInfo,
  apiRemoveFromFolders,
  apiRestoreFromArchiveFileInfo,
} from "api/common/fileinfo.service";
import { apiGetAISource, deleteAISourceByFileInfoId, postSourcesUpdate } from "api/ai/ai.service";
import Common from "api/common/common";
import { queryKey } from "api/api-utils";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import SlowButton from "components/SlowButton";
import { useState, useEffect } from "react";
import AIProgressWidget from "../AITasks/AIProgressWidget";
import DevOnly from "context/Auth/DevOnly";
import useAirUserRoles from "../../../utils/AuthUtils";
import PropInput from "components/PropInput";
import { useRightSidebar } from "context/RightSidebarContext";
import userApi, { getUserName } from "api/user-administration/user-administration";

/**
 * @param {Object} p
 * @param {FileInfo} p.item
 */
export default function SourceDetailsRSidebar({ item, folder, readOnly }) {
  const queryClient = useQueryClient();
  const { doClose } = useRightSidebar();
  const { isController } = useAirUserRoles();
  const { colors } = useTheme();

  const [inRound, setInRound] = useState();
  useEffect(() => {
    setInRound(item.folders?.includes(folder));
  }, [item, item.folders, folder]);

  const toggleInRound = () => {
    const p = inRound ? apiRemoveFromFolders(item, folder) : apiAddToFolders(item, folder);
    setInRound(!inRound);
    return p.then(() => {
      queryClient.invalidateQueries("fileinfoService.list");
    });
  };

  const toggleArchive = () => {
    let p;
    if (item.archived) {
      p = apiRestoreFromArchiveFileInfo(item);
      p.then(() => {
        postSourcesUpdate([item.id], true);
      });
    } else {
      p = apiArchiveFileInfo(item);
    }
    return p.then(() => {
      queryClient.invalidateQueries("fileinfoService.list");
      queryClient.invalidateQueries(["fileinfoService.list", "archived"]);
    });
  };

  const { data: aiSource } = useQuery({
    queryKey: queryKey("apiGetSource", item.id),
    queryFn: () =>
      apiGetAISource(item).then((data) => {
        if (data?.periodStart) item.periodStart = data.periodStart;
        if (data?.periodEnd) item.periodEnd = data.periodEnd;
        return data || null;
      }).catch(() => null),
    enabled: !!item.id,
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
  });

  const { data: uploadedByUser } = useQuery({
    queryKey: ["user", item.createdBy],
    queryFn: () => userApi.getUserById(item.createdBy),
    enabled: !!item.createdBy,
  });

  const handleDeleteAI = () => {
    return deleteAISourceByFileInfoId(item.id).then(() => {
      queryClient.invalidateQueries(queryKey("apiGetSource", item.id));
    });
  };

  const [aiAnalysisTask, setAiAnalysisTask] = useState();
  const handleSendForAnalysis = () => {
    return postSourcesUpdate([item.id], false).then((data) => {
      setAiAnalysisTask(data);
      queryClient.invalidateQueries(queryKey("apiGetSource", item.id));
    });
  };

  const handleEdit = (prop, value) => {
    fileinfoService.update(item.id, item).then(() => {
      queryClient.invalidateQueries(queryKey("apiGetSource", item.id));
    });
  };

  const handleDownload = () => {
    Common.getAttachment({ attachmentId: item.documentId });
  };

  return (
    <Box id="SourceDetailsPanel" display="flex" flexDirection="column" h="full" mx={4}>
      <Tabs variant="primary" display="flex" flexDirection="column" flex={1} minH={0}>
        <TabList borderBottom="1px solid" borderColor="gray.200" flexShrink={0}>
          <Tab>Manage</Tab>
        </TabList>

        <TabPanels flex={1} overflowY="auto" minH={0}>
          <TabPanel px={0} pt={4}>
      <Flex direction="column" gap={6} flex={1} minW={0}>
        {/* File info section */}
        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <FileText size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Filename</Text>
          </Flex>
          <Text fontSize="body.s" fontWeight="bold" color="darkTeal">{item.filename}</Text>
        </Flex>

        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <FileText size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Type</Text>
          </Flex>
          <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" colorScheme="gray" width="fit-content">
            {getFileType(item)}
            {item.size && ` — ${item.size > 1024 * 1024
              ? `${(item.size / (1024 * 1024)).toFixed(1)} MB`
              : `${Math.round(item.size / 1024)} KB`}`}
          </Badge>
        </Flex>

        {/* Uploaded section */}
        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <UserCircle size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Uploaded</Text>
          </Flex>
          <Badge
            px={2}
            py={1}
            borderRadius="md"
            fontSize="body.xxs"
            colorScheme="orange"
            width="fit-content"
          >
            {uploadedByUser ? getUserName(uploadedByUser) : (item.createdBy || "Unknown")}
            {item.createdDate && ` — ${formatDate(item.createdDate)}`}
          </Badge>
        </Flex>

        {/* AI Analysis section */}
        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <Sparkle size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">AI Analysis</Text>
            <DevOnly>
              <SlowButton size="xs" variant="secondary" isDisabled={!!aiAnalysisTask} onClick={handleSendForAnalysis} ml="auto">
                Re-analyse
              </SlowButton>
              {aiAnalysisTask && <AIProgressWidget taskId={aiAnalysisTask?.id} />}
            </DevOnly>
          </Flex>
          <Badge
            px={2}
            py={1}
            borderRadius="md"
            fontSize="body.xxs"
            colorScheme={item.aiDate ? "green" : "gray"}
            width="fit-content"
          >
            {item.aiDate ? `Complete — ${formatDate(item.aiDate)}` : "Not complete"}
          </Badge>
        </Flex>

        {aiSource?.summary && (
          <Flex direction="column" gap={2}>
            <Flex gap={2} align="center">
              <Sparkle size={18} color={colors.gray[400]} />
              <Text variant="statusTitle">Summary</Text>
            </Flex>
            <Text fontSize="body.s" color="gray.700" textAlign="left">{aiSource.summary}</Text>
          </Flex>
        )}

        {aiSource?.error && (
          <Flex direction="column" gap={2}>
            <Flex gap={2} align="center">
              <WarningDiamond size={18} color={colors.red[400]} />
              <Text variant="statusTitle" color="red.600">Analysis Issue</Text>
            </Flex>
            <Text fontSize="sm" color="red.500">{aiSource.error}</Text>
          </Flex>
        )}

        {aiSource?.regions?.length > 0 && (
          <Flex direction="column" gap={2}>
            <Flex gap={2} align="center">
              <GlobeHemisphereWest size={18} color={colors.gray[400]} />
              <Text variant="statusTitle">Regions</Text>
            </Flex>
            <Text fontSize="sm" color="gray.700">{aiSource.regions.join(", ")}</Text>
          </Flex>
        )}

        {/* Period section */}
        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <CalendarBlank size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Reporting Period</Text>
          </Flex>
          <HStack spacing={4}>
            <PropInput
              inline
              label="Start"
              prop="periodStart"
              item={item}
              type="date"
              size="sm"
              fontSize="xs"
              borderRadius="full"
              onChange={(e) => handleEdit("periodStart", e.target.value)}
              max={item?.periodEnd}
            />
            <PropInput
              inline
              label="End"
              prop="periodEnd"
              item={item}
              type="date"
              size="sm"
              fontSize="xs"
              borderRadius="full"
              onChange={(e) => handleEdit("periodEnd", e.target.value)}
              min={item?.periodStart}
            />
          </HStack>
        </Flex>

        <DevOnly>
          <Flex direction="column" gap={2}>
            <Text variant="statusTitle">File ID</Text>
            <Text fontSize="xs" color="gray.500" fontFamily="mono" wordBreak="break-all">{item.id}</Text>
          </Flex>
          <Flex direction="column" gap={2} mt={4}>
            <Text variant="statusTitle">Folders</Text>
            <Text fontSize="xs" color="gray.500" wordBreak="break-all">{item.folders?.join(", ")}</Text>
          </Flex>
        </DevOnly>

      </Flex>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Box pt={4} flexShrink={0} borderTop="1px solid" borderColor="gray.200">
        <VStack spacing={2}>
          <Button
            w="full"
            variant="secondary"
            onClick={handleDownload}
          >
            Download File
          </Button>
          {readOnly && !inRound && (
            <SlowButton
              w="full"
              variant="primary"
              onClick={toggleInRound}
            >
              Add to Round
            </SlowButton>
          )}
          {!readOnly && isController() && (
            <>
              <SlowButton
                w="full"
                variant={inRound ? "secondary" : "primary"}
                onClick={toggleInRound}
              >
                {inRound ? "Remove from Round" : "Add to Round"}
              </SlowButton>
              {!item.archived && (
                <SlowButton
                  w="full"
                  variant="secondary"
                  onClick={toggleArchive}
                >
                  Archive
                </SlowButton>
              )}
              {item.archived && (
                <SlowButton
                  w="full"
                  variant="secondary"
                  onClick={toggleArchive}
                >
                  Restore from Archive
                </SlowButton>
              )}
            </>
          )}
          <Button w="full" variant="secondary" onClick={doClose}>
            Close
          </Button>
        </VStack>
      </Box>
    </Box>
  );
}
